import {messages} from '@smartmixin/i18n';
import {Globe} from '@phosphor-icons/react';
import {Box, MenuItem, Select, SelectChangeEvent} from '@mui/material';
import {useIntl} from 'react-intl';
import {useLocation, useNavigate} from 'react-router';
import {
  HELP_CONTENT_LOCALES,
  HELP_LOCALE_NAMES,
  helpPathForLocale,
  parseHelpLocaleFromPath,
} from '../locales';

/**
 * Deep-links between the translations of the current help page: switching
 * locale keeps the visitor on the same article/category, swapping only the URL
 * prefix (`/help/…` ↔ `/help/fr/…`). Query (`?platform=mobile`) and hash
 * deep links are preserved. Locale names are shown in their own language on
 * purpose — a visitor lost in the wrong language must be able to find theirs.
 */
const HelpLanguageSwitcher = () => {
  const navigate = useNavigate();
  const {pathname, search, hash} = useLocation();
  const {formatMessage} = useIntl();
  const current = parseHelpLocaleFromPath(pathname) ?? 'en';

  const handleChange = (event: SelectChangeEvent) => {
    const target = event.target.value;
    if (target !== current) {
      navigate(`${helpPathForLocale(pathname, target)}${search}${hash}`);
    }
  };

  return (
    <Select
      value={current}
      onChange={handleChange}
      size="small"
      variant="outlined"
      inputProps={{'aria-label': formatMessage(messages.settings.language)}}
      startAdornment={
        <Box
          component="span"
          aria-hidden
          sx={{display: 'flex', mr: 0.75, color: 'text.secondary'}}>
          <Globe size={16} />
        </Box>
      }
      sx={{
        typography: 'body2',
        color: 'text.primary',
        bgcolor: 'background.paper',
        '& .MuiSelect-select': {py: '7px'},
      }}>
      {HELP_CONTENT_LOCALES.map((locale) => (
        <MenuItem key={locale} value={locale}>
          {HELP_LOCALE_NAMES[locale]}
        </MenuItem>
      ))}
    </Select>
  );
};

export default HelpLanguageSwitcher;
